import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  MapPin,
  Plus,
  RefreshCw,
  Search,
  Settings,
  Trash2,
  Lock,
  Unlock,
  Pencil,
  Activity,
  X,
  ChevronRight,
  Users,
  Navigation,
} from "lucide-react";

import {
  useAdminGetCentersQuery,
  useAdminCreateCenterMutation,
  useAdminUpdateCenterMutation,
  useAdminDeleteCenterMutation,
} from "../../api/examsApi";

// Security & UI Patterns
import PageGate from "../../../../components/PageGate";
import AccessGate from "../../../../components/AccessGate";
import { PERMS } from "../../../../utils/perms";

function safeMsg(err: any) {
  return (
    err?.data?.message ||
    err?.data?.error ||
    err?.error ||
    err?.message ||
    "Something went wrong"
  );
}

/** * Hover-Triggered Tooltip */
function Tooltip({
  children,
  text,
}: {
  children: React.ReactNode;
  text: string;
}) {
  return (
    <div className="relative group flex items-center justify-center">
      {children}
      <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-[60] pointer-events-none animate-in fade-in zoom-in-95 duration-100">
        <div className="bg-gray-900 text-white text-[9px] font-black uppercase tracking-[0.15em] px-2 py-1.5 rounded shadow-xl whitespace-nowrap border border-gray-700">
          {text}
        </div>
        <div className="w-2 h-2 bg-gray-900 rotate-45 mx-auto -mt-1 border-r border-b border-gray-700"></div>
      </div>
    </div>
  );
}

export default function AdminCentersPage() {
  const {
    data = [],
    isLoading,
    error,
    refetch,
    isFetching,
  } = useAdminGetCentersQuery();
  const [createCenter, { isLoading: creating }] =
    useAdminCreateCenterMutation();
  const [updateCenter, { isLoading: updating }] =
    useAdminUpdateCenterMutation();
  const [deleteCenter, { isLoading: deleting }] =
    useAdminDeleteCenterMutation();

  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<any | null>(null);
  const [msg, setMsg] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);

  const [form, setForm] = useState({
    name: "",
    code: "",
    state: "",
    address: "",
    capacity: "",
    is_active: true,
  });

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return data.filter((c: any) =>
      `${c.name} ${c.state ?? ""} ${c.address ?? ""}`.toLowerCase().includes(s)
    );
  }, [data, q]);

  const stats = useMemo(
    () => ({
      total: data.length,
      active: data.filter((x: any) => x.is_active).length,
      totalCapacity: data.reduce(
        (acc: number, curr: any) => acc + (Number(curr.capacity) || 0),
        0
      ),
    }),
    [data]
  );

  const onCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    setMsg(null);
    try {
      await createCenter({
        ...form,
        capacity: form.capacity ? Number(form.capacity) : null,
      } as any).unwrap();
      setMsg({
        type: "success",
        text: "Physical center registered in network",
      });
      setForm({
        name: "",
        code: "",
        state: "",
        address: "",
        capacity: "",
        is_active: true,
      });
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    }
  };

  const handleUpdate = async (id: string, body: any) => {
    setBusyId(id);
    try {
      await updateCenter({ id, body }).unwrap();
      setMsg({ type: "success", text: "Center parameters updated" });
      setEditTarget(null);
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (x: any) => {
    if (!window.confirm(`CRITICAL: Remove "${x.name}" from logistics network?`))
      return;
    setBusyId(x.id);
    try {
      await deleteCenter(x.id).unwrap();
      setMsg({ type: "success", text: "Center purged successfully" });
    } catch (err) {
      setMsg({ type: "error", text: safeMsg(err) });
    } finally {
      setBusyId(null);
    }
  };

  return (
    <PageGate perm={PERMS.exam.centerAccess}>
      <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 pb-20">
        {/* 1. Network Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[
            {
              label: "Network Nodes",
              val: stats.total,
              icon: MapPin,
              color: "indigo",
            },
            {
              label: "Operational",
              val: stats.active,
              icon: Activity,
              color: "emerald",
            },
            {
              label: "Global Capacity",
              val: stats.totalCapacity.toLocaleString(),
              icon: Users,
              color: "amber",
            },
          ].map((s, i) => (
            <div
              key={i}
              className="bg-white border border-gray-100 p-6 rounded-3xl shadow-sm flex items-center gap-4"
            >
              <div
                className={`w-12 h-12 rounded-2xl flex items-center justify-center bg-${s.color}-50 text-${s.color}-600`}
              >
                <s.icon className="w-6 h-6" />
              </div>
              <div>
                <div className="text-[10px] font-black text-gray-400 uppercase tracking-widest mb-1">
                  {s.label}
                </div>
                <div className="text-xl font-black text-gray-900 tracking-tighter">
                  {s.val}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* 2. Header Section */}
        <div className="bg-white border border-gray-100 rounded-3xl p-8 shadow-sm">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex items-center gap-6">
              <div className="w-16 h-16 rounded-2xl bg-gray-900 flex items-center justify-center text-white shadow-xl">
                <Navigation className="w-8 h-8" />
              </div>
              <div>
                <h1 className="text-3xl font-black text-gray-900 tracking-tight uppercase">
                  Center Logistics
                </h1>
                <p className="text-gray-400 text-sm mt-2 font-medium">
                  Management of physical examination venues and seating
                  metadata.
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Link
                to="/exams/admin/exams"
                className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all"
              >
                Exams
              </Link>
              <Link
                to="/exams/admin/exam-center-slots"
                className="px-4 py-2 rounded-xl border bg-white hover:bg-gray-50 text-[10px] font-black uppercase tracking-widest transition-all"
              >
                Center Slots
              </Link>
              <Tooltip text="Refresh Database">
                <button
                  onClick={() => refetch()}
                  className="p-3 rounded-xl border bg-white hover:bg-gray-50"
                >
                  <RefreshCw
                    className={`w-4 h-4 text-indigo-500 ${
                      isFetching && "animate-spin"
                    }`}
                  />
                </button>
              </Tooltip>
            </div>
          </div>

          {msg && (
            <div
              className={`mt-6 p-4 rounded-2xl border flex justify-between items-center animate-in slide-in-from-top-2 ${
                msg.type === "success"
                  ? "bg-emerald-50 border-emerald-100 text-emerald-800"
                  : "bg-rose-50 border-rose-100 text-rose-800"
              }`}
            >
              <span className="text-[10px] font-black uppercase tracking-widest">
                {msg.text}
              </span>
              <button onClick={() => setMsg(null)}>
                <X className="w-4 h-4" />
              </button>
            </div>
          )}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* 3. Creation Form */}
          <AccessGate perm={PERMS.exam.centerCreate}>
            <div className="lg:col-span-1">
              <div className="bg-white border border-gray-100 rounded-3xl p-6 shadow-sm sticky top-6">
                <div className="flex items-center gap-2 mb-6 border-b pb-4">
                  <Plus className="w-4 h-4 text-indigo-500" />
                  <div className="font-black text-gray-900 text-[10px] uppercase tracking-widest">
                    Register Venue
                  </div>
                </div>

                <form onSubmit={onCreate} className="space-y-5">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Venue Name
                    </label>
                    <input
                      placeholder="e.g. Lagos West Annex"
                      value={form.name}
                      onChange={(e) =>
                        setForm({ ...form, name: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      required
                    />
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Center Code
                    </label>
                    <input
                      placeholder="e.g. LW-001"
                      value={form.code}
                      onChange={(e) =>
                        setForm({ ...form, code: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        State/Region
                      </label>
                      <input
                        placeholder="Lagos"
                        value={form.state}
                        onChange={(e) =>
                          setForm({ ...form, state: e.target.value })
                        }
                        className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                        Max Seating
                      </label>
                      <input
                        type="number"
                        placeholder="0"
                        value={form.capacity}
                        onChange={(e) =>
                          setForm({ ...form, capacity: e.target.value })
                        }
                        className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                      />
                    </div>
                  </div>

                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Physical Address
                    </label>
                    <textarea
                      placeholder="Street, Building, etc."
                      value={form.address}
                      onChange={(e) =>
                        setForm({ ...form, address: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent resize-none"
                      rows={2}
                    />
                  </div>

                  <label className="flex items-center gap-3 cursor-pointer group py-2">
                    <input
                      type="checkbox"
                      checked={form.is_active}
                      onChange={(e) =>
                        setForm({ ...form, is_active: e.target.checked })
                      }
                      className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                    />
                    <span className="text-[10px] font-black text-gray-500 uppercase tracking-widest group-hover:text-gray-900 transition-colors">
                      Operational Status
                    </span>
                  </label>

                  <Tooltip text="Provision Center">
                    <button
                      disabled={creating}
                      className="w-full bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] hover:bg-indigo-600 transition-all disabled:opacity-30 active:scale-95"
                    >
                      {creating ? "Communicating..." : "Initialize Venue"}
                    </button>
                  </Tooltip>
                </form>
              </div>
            </div>
          </AccessGate>

          {/* 4. Centers Table */}
          <div className="lg:col-span-2">
            <div className="bg-white border border-gray-100 rounded-[2.5rem] shadow-sm overflow-hidden">
              <div className="p-4 bg-gray-50/50 border-b flex items-center gap-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Filter network nodes..."
                    className="w-full pl-10 pr-4 py-2 bg-white border border-gray-200 rounded-xl text-xs font-bold focus:ring-4 ring-indigo-50 outline-none"
                  />
                </div>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="text-left bg-white border-b">
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Center Information
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Logistics
                      </th>
                      <th className="px-8 py-5 text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] text-center">
                        Status
                      </th>
                      <th className="px-8 py-5 text-right text-[10px] font-black text-gray-400 uppercase tracking-[0.2em]">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-50">
                    {rows.map((c: any) => (
                      <tr
                        key={c.id}
                        className="group hover:bg-gray-50/80 transition-all"
                      >
                        <td className="px-8 py-5">
                          <div className="font-black text-gray-900 uppercase tracking-tight group-hover:text-indigo-600 flex items-center gap-2">
                            {c.name}
                          </div>
                          {c.code && (
                            <div className="text-[9px] font-black text-indigo-500 mt-0.5 uppercase tracking-widest">
                              {c.code}
                            </div>
                          )}
                          <div className="text-[9px] font-bold text-gray-400 mt-1 uppercase tracking-widest max-w-[200px] truncate">
                            {c.address || "No Address Provided"}
                          </div>
                        </td>
                        <td className="px-8 py-5">
                          <div className="text-[10px] font-black text-gray-500 uppercase">
                            {c.state || "N/A"}
                          </div>
                          <div className="flex items-center gap-1 text-[9px] font-bold text-gray-400 uppercase tracking-tighter mt-1">
                            <Users className="w-3 h-3" /> CAP:{" "}
                            {c.capacity ?? "—"}
                          </div>
                        </td>
                        <td className="px-8 py-5 text-center">
                          <span
                            className={`px-3 py-1 rounded-full text-[9px] font-black uppercase tracking-widest border ${
                              c.is_active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-100"
                                : "bg-gray-50 text-gray-400 border-gray-100"
                            }`}
                          >
                            {c.is_active ? "Operational" : "Offline"}
                          </span>
                        </td>
                        <td className="px-8 py-5 text-right">
                          <div className="flex justify-end gap-1 items-center">
                            <Tooltip text="View Allocation Slots">
                              <Link
                                to={`/admin/exams/center-slots?centerId=${c.id}`}
                                className="p-2.5 text-gray-400 hover:text-indigo-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                              >
                                <ChevronRight className="w-4 h-4" />
                              </Link>
                            </Tooltip>
                            <AccessGate perm={PERMS.exam.centerEdit}>
                              <Tooltip text="Edit Parameters">
                                <button
                                  onClick={() => setEditTarget(c)}
                                  className="p-2.5 text-gray-400 hover:text-blue-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  <Pencil className="w-4 h-4" />
                                </button>
                              </Tooltip>
                              <Tooltip
                                text={
                                  c.is_active ? "Decommission" : "Reactivate"
                                }
                              >
                                <button
                                  onClick={() =>
                                    handleUpdate(c.id, {
                                      is_active: !c.is_active,
                                    })
                                  }
                                  className="p-2.5 text-gray-400 hover:text-amber-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  {c.is_active ? (
                                    <Lock className="w-4 h-4" />
                                  ) : (
                                    <Unlock className="w-4 h-4" />
                                  )}
                                </button>
                              </Tooltip>
                            </AccessGate>
                            <AccessGate perm={PERMS.exam.centerDelete}>
                              <Tooltip text="Purge Node">
                                <button
                                  onClick={() => handleDelete(c)}
                                  className="p-2.5 text-gray-400 hover:text-rose-600 hover:bg-white rounded-xl border border-transparent hover:border-gray-100 transition-all"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </button>
                              </Tooltip>
                            </AccessGate>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>

        {/* 5. Modern Edit Modal */}
        {editTarget && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            <div
              className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm"
              onClick={() => setEditTarget(null)}
            />
            <div className="relative w-full max-w-lg bg-white rounded-[2.5rem] shadow-2xl overflow-hidden animate-in zoom-in-95 duration-200">
              <div className="p-8 border-b border-gray-50 flex justify-between items-center bg-gray-50/30">
                <div className="flex items-center gap-3">
                  <div className="p-2.5 bg-gray-900 rounded-2xl text-white shadow-lg">
                    <Pencil className="w-5 h-5" />
                  </div>
                  <div>
                    <h3 className="font-black text-gray-900 text-sm uppercase tracking-tighter leading-none">
                      Modify Center
                    </h3>
                    <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-1.5">
                      Network Node Configuration
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setEditTarget(null)}
                  className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                >
                  <X className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              <div className="p-8 space-y-6">
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Venue Title
                  </label>
                  <input
                    value={editTarget.name}
                    onChange={(e) =>
                      setEditTarget({ ...editTarget, name: e.target.value })
                    }
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Center Code
                  </label>
                  <input
                    value={editTarget.code ?? ""}
                    onChange={(e) =>
                      setEditTarget({ ...editTarget, code: e.target.value })
                    }
                    className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      State
                    </label>
                    <input
                      value={editTarget.state}
                      onChange={(e) =>
                        setEditTarget({ ...editTarget, state: e.target.value })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                      Capacity
                    </label>
                    <input
                      type="number"
                      value={editTarget.capacity}
                      onChange={(e) =>
                        setEditTarget({
                          ...editTarget,
                          capacity: e.target.value,
                        })
                      }
                      className="w-full border-b-2 border-gray-100 py-2 font-black text-gray-800 outline-none focus:border-indigo-500 bg-transparent"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <label className="text-[10px] font-black text-gray-400 uppercase tracking-widest">
                    Address
                  </label>
                  <textarea
                    value={editTarget.address}
                    onChange={(e) =>
                      setEditTarget({ ...editTarget, address: e.target.value })
                    }
                    className="w-full border-b-2 border-gray-100 py-2 text-sm font-bold text-gray-800 outline-none focus:border-indigo-500 bg-transparent resize-none"
                    rows={2}
                  />
                </div>
              </div>

              <div className="p-8 bg-gray-50 flex gap-3">
                <button
                  onClick={() => setEditTarget(null)}
                  className="flex-1 py-4 text-[10px] font-black uppercase tracking-widest text-gray-400 hover:text-gray-600 transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() =>
                    handleUpdate(editTarget.id, {
                      ...editTarget,
                      capacity: Number(editTarget.capacity),
                    })
                  }
                  disabled={updating}
                  className="flex-[2] bg-gray-900 text-white rounded-2xl py-4 text-[10px] font-black uppercase tracking-[0.2em] shadow-xl shadow-gray-200 hover:bg-indigo-600 transition-all"
                >
                  {updating ? "Syncing..." : "Update Node"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageGate>
  );
}
